/**
 * 서버 이미지 URL을 상대 경로로 변환한다.
 * 외부 도메인(구글, 애플 등) URL은 변환하지 않고 그대로 반환한다.
 * localhost에서는 절대 URL을 그대로 사용한다.
 *
 * e.g. "http://158.179.170.39:8080/image/feed/files/xxx.jpg" → "/image/feed/files/xxx.jpg"
 *
 * 판단 기준:
 *  1. pathname이 /image/ 로 시작하면 내부 이미지 서버 URL → 상대경로로 변환
 *     (서버 도메인이 IP·도메인·포트 무관하게 일관 적용)
 *  2. 현재 호스트와 동일하면 상대경로 변환
 *  3. 그 외 외부 도메인(소셜 프로필 등)은 원본 반환
 */
export const toRelativeImageUrl = (url: string): string => {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') return url;
  try {
    const parsed = new URL(url);
    // /image/ 경로 = 내부 이미지 서버 — 도메인(IP/도메인명/포트) 불문 상대경로 변환
    if (parsed.pathname.startsWith('/image/')) {
      return parsed.pathname + parsed.search;
    }
    // 현재 호스트와 동일한 URL도 상대경로 변환
    const currentHost = typeof window !== 'undefined' ? window.location.host : '';
    if (currentHost && parsed.host === currentHost) {
      return parsed.pathname + parsed.search;
    }
    // 외부 도메인(구글, 애플 등 소셜 프로필 이미지)은 변환하지 않음
    return url;
  } catch {
    return url;
  }
};

const MAX_WIDTH = 1200;
const MAX_HEIGHT = 1200;
const JPEG_QUALITY = 0.8;
const MAX_IMAGE_COUNT = 5;

export { MAX_IMAGE_COUNT };

/**
 * 이미지 파일을 최대 1200x1200 크기로 리사이즈하고 JPEG 0.8 품질로 압축한다.
 * 원본이 제한 크기 이하라면 리사이즈 없이 압축만 수행한다.
 */
export const resizeImage = (file: File): Promise<File> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context를 생성할 수 없습니다.'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('이미지 압축에 실패했습니다.'));
            return;
          }
          const resizedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        },
        'image/jpeg',
        JPEG_QUALITY,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('이미지 로드에 실패했습니다.'));
    };

    img.src = url;
  });

/**
 * 여러 이미지를 병렬로 리사이즈한다.
 */
export const resizeImages = (files: File[]): Promise<File[]> => Promise.all(files.map(resizeImage));
